import { Box, Divider, Drawer, Typography } from '@mui/material'
import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  startOfDay,
  endOfDay,
  subDays,
  startOfWeek,
  endOfWeek,
  subWeeks,
  startOfMonth,
  endOfMonth,
  subMonths,
} from 'date-fns'
import { useForm, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'

import { resolve } from '@hapana/shared/web/services/gqty'
import Accordion from '@hapana/shared/web/components/shared/accordion/accordion.component'
import Dropdown from '@hapana/shared/web/components/shared/dropdown/dropdown.component'
import Button from '@hapana/shared/web/components/shared/button/button.component'
import {
  BUTTON_VARIANTS,
  BUTTON_SIZE,
  BUTTON_THEME,
} from '@hapana/shared/web/components/shared/button/button.constants'
import Loading from '@hapana/shared/web/components/shared/loading/loading.component'
import DateRangePicker, {
  START_DATE,
} from '@hapana/shared/web/components/shared/date-picker/date-range-picker/date-range-picker.component'
import CloseSmallIcon from '@hapana/shared/web/styles/assets/svgs/close-icon.svg'

import { styles } from './history-filter-dialog.styles'

const DATE_PRESETS = [
  {
    label: 'checkIn:equipments.history.filter.today',
    getRange: (tz) => {
      const now = utcToZonedTime(new Date(), tz)
      return {
        startDate: zonedTimeToUtc(startOfDay(now), tz),
        endDate: zonedTimeToUtc(endOfDay(now), tz),
      }
    },
  },
  {
    label: 'checkIn:equipments.history.filter.yesterday',
    getRange: (tz) => {
      const yesterday = subDays(utcToZonedTime(new Date(), tz), 1)
      return {
        startDate: zonedTimeToUtc(startOfDay(yesterday), tz),
        endDate: zonedTimeToUtc(endOfDay(yesterday), tz),
      }
    },
  },
  {
    label: 'checkIn:equipments.history.filter.thisWeek',
    getRange: (tz) => {
      const now = utcToZonedTime(new Date(), tz)
      return {
        startDate: zonedTimeToUtc(startOfDay(startOfWeek(now)), tz),
        endDate: zonedTimeToUtc(endOfDay(endOfWeek(now)), tz),
      }
    },
  },
  {
    label: 'checkIn:equipments.history.filter.lastWeek',
    getRange: (tz) => {
      const lastWeek = subWeeks(utcToZonedTime(new Date(), tz), 1)
      return {
        startDate: zonedTimeToUtc(startOfDay(startOfWeek(lastWeek)), tz),
        endDate: zonedTimeToUtc(endOfDay(endOfWeek(lastWeek)), tz),
      }
    },
  },
  {
    label: 'checkIn:equipments.history.filter.thisMonth',
    getRange: (tz) => {
      const now = utcToZonedTime(new Date(), tz)
      return {
        startDate: zonedTimeToUtc(startOfDay(startOfMonth(now)), tz),
        endDate: zonedTimeToUtc(endOfDay(endOfMonth(now)), tz),
      }
    },
  },
  {
    label: 'checkIn:equipments.history.filter.lastMonth',
    getRange: (tz) => {
      const lastMonth = subMonths(utcToZonedTime(new Date(), tz), 1)
      return {
        startDate: zonedTimeToUtc(startOfDay(startOfMonth(lastMonth)), tz),
        endDate: zonedTimeToUtc(endOfDay(endOfMonth(lastMonth)), tz),
      }
    },
  },
]

const filterSchema = z.object({ clientId: z.string().optional() })

const HistoryFilterDialog = ({ open, onClose, onApply, currentFilters, siteId, siteTimezone }) => {
  const { t } = useTranslation(['checkIn'])
  const formMethods = useForm({
    resolver: zodResolver(filterSchema),
    defaultValues: { clientId: currentFilters.clientId ?? '' },
  })

  const [draftFilters, setDraftFilters] = useState(currentFilters)
  const [clientOptions, setClientOptions] = useState([])
  const [isLoadingClients, setIsLoadingClients] = useState(false)
  const [clientOpen, setClientOpen] = useState(true)
  const [dateOpen, setDateOpen] = useState(true)

  // Sync form and draft state each time the drawer opens
  useEffect(() => {
    if (!open) return
    setDraftFilters(currentFilters)
    formMethods.setValue('clientId', currentFilters.clientId ?? '')
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load client options once when siteId is available
  useEffect(() => {
    if (!siteId) return
    setIsLoadingClients(true)
    resolve(
      ({ query }) => {
        const result = query.clients({ search: '', pageSize: 100, siteId })
        return (
          result?.results
            ?.map((r) => {
              const c = r?.row
              if (!c) return null
              const label = `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || (c.email ?? '')
              return { label, value: c.id ?? '' }
            })
            .filter(Boolean) ?? []
        )
      },
      { cachePolicy: 'no-cache' }
    )
      .then((options) => {
        setClientOptions(options)
        setIsLoadingClients(false)
      })
      .catch(() => {
        setClientOptions([])
        setIsLoadingClients(false)
      })
  }, [siteId])

  const handleApply = () => {
    const clientId = formMethods.getValues('clientId') || null
    onApply({ ...draftFilters, clientId })
    onClose()
  }

  const handleClear = () => {
    formMethods.setValue('clientId', '')
    const tz = siteTimezone || 'UTC'
    const nowInSiteZone = utcToZonedTime(new Date(), tz)
    const defaultFilters = {
      startDate: zonedTimeToUtc(startOfDay(subDays(nowInSiteZone, 7)), tz),
      endDate: zonedTimeToUtc(endOfDay(nowInSiteZone), tz),
      clientId: null,
    }
    setDraftFilters(defaultFilters)
    onApply(defaultFilters)
    onClose()
  }

  const handleDateChange = (field, value) => {
    const tz = siteTimezone || 'UTC'
    const calDay = new Date(value.getFullYear(), value.getMonth(), value.getDate())
    if (field === START_DATE) {
      setDraftFilters((prev) => ({
        ...prev,
        startDate: zonedTimeToUtc(startOfDay(calDay), tz),
        endDate: zonedTimeToUtc(endOfDay(calDay), tz),
      }))
    } else {
      setDraftFilters((prev) => ({
        ...prev,
        endDate: zonedTimeToUtc(endOfDay(calDay), tz),
      }))
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      disablePortal
      PaperProps={{ elevation: 4, sx: styles.drawerPaper }}
      ModalProps={{
        disableScrollLock: true,
        BackdropProps: { sx: styles.drawerBackdrop },
      }}
    >
      <FormProvider {...formMethods}>
        <Box sx={styles.drawerHeader}>
          <Typography typography="header2" color="gray.100">
            {t('checkIn:equipments.history.filter.title')}
          </Typography>
          <Button
            variant={BUTTON_VARIANTS.ICON}
            icon={CloseSmallIcon}
            size={BUTTON_SIZE.SMALL}
            onClick={onClose}
          />
        </Box>

        <Box sx={styles.drawerContent}>
          <Accordion
            label={t('checkIn:equipments.history.filter.clientSection')}
            isOpen={clientOpen}
            onChange={() => setClientOpen((v) => !v)}
            content={
              <Box sx={styles.sectionContent}>
                {isLoadingClients ? (
                  <Loading />
                ) : (
                  <Dropdown
                    name="clientId"
                    label=""
                    shouldSearch
                    placeholder={t('checkIn:equipments.history.filter.searchClient')}
                    options={clientOptions}
                    displayEmpty
                    dropdownPlaceholder={t('checkIn:equipments.history.filter.selectClient')}
                  />
                )}
              </Box>
            }
          />

          <Divider />

          <Accordion
            label={t('checkIn:equipments.history.filter.dateSection')}
            isOpen={dateOpen}
            onChange={() => setDateOpen((v) => !v)}
            content={
              <Box sx={styles.sectionContent}>
                <Box sx={styles.presetPills}>
                  {DATE_PRESETS.map((preset) => (
                    <Button
                      key={preset.label}
                      variant={BUTTON_VARIANTS.OUTLINED}
                      size={BUTTON_SIZE.SMALL}
                      theme={BUTTON_THEME.GRAY}
                      label={t(preset.label)}
                      onClick={() =>
                        setDraftFilters((prev) => ({
                          ...prev,
                          ...preset.getRange(siteTimezone || 'UTC'),
                        }))
                      }
                    />
                  ))}
                </Box>
                <Box sx={styles.orDivider}>
                  <Divider sx={styles.orLine} />
                  <Typography typography="medium" color="gray.64">
                    {t('checkIn:equipments.history.filter.or')}
                  </Typography>
                  <Divider sx={styles.orLine} />
                </Box>
                {draftFilters.startDate && draftFilters.endDate && (
                  <DateRangePicker
                    key={`${draftFilters.startDate.getTime()}-${draftFilters.endDate.getTime()}`}
                    initialStartDate={utcToZonedTime(draftFilters.startDate, siteTimezone || 'UTC')}
                    initialEndDate={utcToZonedTime(draftFilters.endDate, siteTimezone || 'UTC')}
                    onDateChange={handleDateChange}
                    maxDate={zonedTimeToUtc(
                      `${formatInTimeZone(new Date(), siteTimezone || 'UTC', 'yyyy-MM-dd')}T23:59:59.999`,
                      siteTimezone || 'UTC'
                    )}
                    layout="horizontal"
                    displayFormat="EEE MMM dd yyyy"
                    timezone={siteTimezone}
                  />
                )}
              </Box>
            }
          />
        </Box>

        <Box sx={styles.drawerFooter}>
          <Button
            variant={BUTTON_VARIANTS.OUTLINED}
            size={BUTTON_SIZE.MEDIUM}
            theme={BUTTON_THEME.GRAY}
            label={t('checkIn:equipments.history.filter.clear')}
            onClick={handleClear}
          />
          <Button
            variant={BUTTON_VARIANTS.CONTAINED}
            size={BUTTON_SIZE.MEDIUM}
            label={t('checkIn:equipments.history.filter.apply')}
            onClick={handleApply}
          />
        </Box>
      </FormProvider>
    </Drawer>
  )
}

export default HistoryFilterDialog
